import { Component } from '@theme/component';

const STORAGE_KEY = 'whatsapp_contact_data';
const EXPIRATION_DAYS = 10;
// Porta site/sdk/regex.ts (EMAIL_REGEX) do repo deco.cx (bolovo-store).
const EMAIL_REGEX = /^(?!\.)(?!.*\.\.)([A-Z0-9_+-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;

/**
 * Botão flutuante do WhatsApp: lista os números, e ao escolher um pede
 * nome/e-mail antes de redirecionar pro WhatsApp. Lembra o lead por
 * `EXPIRATION_DAYS` dias (localStorage) pra pré-preencher o form. Porta
 * components/ui/ButtonWhatsapp.tsx do repo deco.cx (bolovo-store).
 *
 * @typedef {object} ButtonWhatsappRefs
 * @property {HTMLElement} popup
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} numbersStep
 * @property {HTMLElement} formStep
 * @property {HTMLInputElement} nameInput
 * @property {HTMLInputElement} emailInput
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} submitLabel
 *
 * @extends {Component<ButtonWhatsappRefs>}
 */
export class ButtonWhatsappComponent extends Component {
  requiredRefs = ['popup', 'formStep', 'numbersStep'];

  /** @returns {{name: string, email: string, expiresAt: number} | null} */
  #readSavedData() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const data = JSON.parse(stored);
      if (data.expiresAt > Date.now()) return data;

      localStorage.removeItem(STORAGE_KEY);
      return null;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} name
   * @param {string} email
   */
  #saveData(name, email) {
    const data = { name, email, expiresAt: Date.now() + EXPIRATION_DAYS * 24 * 60 * 60 * 1000 };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  /**
   * @param {string} number
   * @param {string} name
   * @param {string} email
   */
  #redirectToWhatsApp(number, name, email) {
    const message = `Olá! Meu nome é ${encodeURIComponent(name)};%0AE-mail: ${encodeURIComponent(email)};`;
    window.open(`https://api.whatsapp.com/send?phone=${number}&text=${message}`, '_blank');
  }

  /** @param {string} [message] */
  #setError(message) {
    const { errorMessage } = this.refs;
    errorMessage.textContent = message ?? '';
    errorMessage.hidden = !message;
  }

  /** @param {'form' | 'numbers'} step */
  #showStep(step) {
    const { formStep, numbersStep } = this.refs;
    formStep.hidden = step !== 'form';
    if (numbersStep) numbersStep.hidden = step !== 'numbers';
  }

  #open() {
    this.refs.popup.hidden = false;
  }

  #close() {
    this.refs.popup.hidden = true;
    this.#selectedNumber = null;
    this.#setError();
    this.#showStep('numbers');
  }

  /** @type {string | null} Número escolhido na lista, usado no redirect após o submit. */
  #selectedNumber = null;

  handleFabClick() {
    if (this.refs.popup.hidden) {
      this.#showStep('numbers');
      this.#open();
    } else {
      this.#close();
    }
  }

  handleClose() {
    this.#close();
  }

  /** @param {Event} event */
  handleNumberSelect(event) {
    const target = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('[data-number]')
    );
    const number = target?.dataset.number;
    if (!number) return;

    this.#selectedNumber = number;
    this.#setError();

    // Pré-preenche com o lead salvo (se houver) pra agilizar o envio.
    const saved = this.#readSavedData();
    if (saved) {
      this.refs.nameInput.value = saved.name;
      this.refs.emailInput.value = saved.email;
    }

    this.#showStep('form');
    this.refs.nameInput.focus();
  }

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();
    this.#setError();

    const name = this.refs.nameInput.value.trim();
    const email = this.refs.emailInput.value.trim();

    if (name.length < 3) {
      this.#setError('Por favor, insira um nome válido.');
      return;
    }

    if (!EMAIL_REGEX.test(email)) {
      this.#setError('Por favor, insira um e-mail válido.');
      return;
    }

    const number = this.#selectedNumber;
    if (!number) {
      this.#showStep('numbers');
      return;
    }

    this.#saveData(name, email);
    this.#redirectToWhatsApp(number, name, email);

    this.refs.nameInput.value = '';
    this.refs.emailInput.value = '';
    this.#close();
  }
}

customElements.define('button-whatsapp-component', ButtonWhatsappComponent);
